import { AttachInternals, Component, Element, Event, EventEmitter, h, Host, Method, Prop, Watch } from '@stencil/core'

import { FloatingUIManager } from '../../utils/floating-ui'
import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/dropdown?tab=designer).
 *
 * @slot trigger - Slot para o elemento que aciona a abertura do dropdown.
 * @slot target - Slot para o conteúdo exibido pelo dropdown.
 */
@Component({
  tag: 'br-dropdown',
  styleUrl: 'dropdown.scss',
  shadow: true,
  formAssociated: true,
})
export class Dropdown {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrDropdownElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * Indica se o dropdown está aberto ou fechado.
   * Esta propriedade é refletida no DOM e pode ser alterada externamente.
   * O valor padrão é falso (fechado).
   */
  @Prop({ reflect: true, mutable: true }) isOpen: boolean = false

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * Define o posicionamento do target (alvo) em relação ao trigger (acionador).
   */
  @Prop({ reflect: true }) placement:
    | 'bottom-start'
    | 'bottom-end'
    | 'top-start'
    | 'top-end'
    | 'left'
    | 'right'
    | 'bottom'
    | 'top' = 'bottom-start'

  /**
   * Define se o dropdown deve permanecer aberto quando outro dropdown é aberto.
   * Quando definido como false (padrão), o dropdown será fechado automaticamente quando outro dropdown for aberto.
   * Quando definido como true, o dropdown permanecerá aberto mesmo quando outro dropdown for aberto.
   */
  @Prop({ reflect: true }) preventAutoDismiss: boolean = false

  /**
   * Define o z-index do elemento target (alvo) do dropdown.
   * Permite customizar a ordem de sobreposição do painel dropdown em relação aos demais elementos da página.
   * O valor padrão utiliza a variável CSS do design system: var(--z-index-layer-1).
   */
  @Prop() targetZIndex: string = 'var(--z-index-layer-1)'

  // Elementos DOM privados
  private triggerElement: HTMLElement
  private targetElement: HTMLElement | null = null
  private dropdownItems: HTMLElement[] = []
  private dropdownLinks: HTMLElement[] = []
  private focusableElement: HTMLElement | null | undefined = null

  // FloatingUI Manager
  private floatingManager: FloatingUIManager

  // Event handlers
  private clickHandler: (event: MouseEvent) => void
  private triggerKeydownHandler: (event: KeyboardEvent) => void
  private targetKeydownHandler: (event: KeyboardEvent) => void

  // Handler para o evento brDidOpen de outros dropdowns
  private dropdownOpenHandler: EventListener

  /**
   * Evento emitido quando o estado do dropdown muda.
   */
  @Event() brDropdownChange: EventEmitter<{ isOpen: boolean }>
  /**
   * Evento emitido quando o dropdown é aberto.
   * Este evento é usado para implementar o auto-dismiss de outros dropdowns.
   */
  @Event() brDidOpen: EventEmitter<{ id: string }>

  /**
   * Método chamado sempre que a propriedade `isOpen` muda.
   * Emite um evento com o novo estado do dropdown.
   */
  @Watch('isOpen')
  isOpenChanged(newValue: boolean) {
    if (newValue) {
      this.brDidOpen.emit({ id: this.customId })

      this.initializeDropdownItems()
      this.positionDropdown()
    } else {
      this.cleanupPositioning()

      if (this.triggerElement !== null && this.triggerElement !== undefined) {
        this.focusOnTriggerElement()
      }
    }

    this.brDropdownChange.emit({ isOpen: newValue })
  }

  /**
   * Aplica o novo valor de z-index ao elemento target.
   */
  @Watch('targetZIndex')
  targetZIndexChanged() {
    this.updateTargetZIndex()
  }

  connectedCallback() {
    this.clickHandler = this.onClickOutside.bind(this)
    document.addEventListener('click', this.clickHandler)

    this.dropdownOpenHandler = this.onDropdownDidOpen.bind(this)
    document.addEventListener('brDidOpen', this.dropdownOpenHandler)
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.clickHandler)
    document.removeEventListener('brDidOpen', this.dropdownOpenHandler)

    if (this.triggerElement !== null && this.triggerElement !== undefined) {
      this.triggerElement.removeEventListener('keydown', this.triggerKeydownHandler)
    }

    if (this.targetElement !== null && this.targetElement !== undefined) {
      this.targetElement.removeEventListener('keydown', this.targetKeydownHandler)
    }

    this.cleanupPositioning()
    this.clearDropdownItemListeners()
  }

  /**
   * Método que lida com o evento brDidOpen de outros dropdowns
   * Fecha este dropdown se não for o emissor do evento e preventAutoDismiss=false
   */
  private onDropdownDidOpen(event: Event) {
    if (this.preventAutoDismiss) {
      return
    }

    const customEvent = event as CustomEvent<{ id: string }>
    if (this.isOpen && customEvent.detail.id !== this.customId) {
      this.hide()
    }
  }

  private clearDropdownItemListeners() {
    this.dropdownItems.forEach((item) => {
      item.removeEventListener('keydown', this.handleItemKeydown)
    })

    this.dropdownLinks.forEach((link) => {
      link.removeEventListener('keydown', this.handleLinkKeydown)
    })

    this.dropdownItems = []
    this.dropdownLinks = []
  }

  componentDidLoad() {
    this.triggerElement = this.el.shadowRoot.querySelector('#trigger')
    this.targetElement = this.el.shadowRoot.querySelector('#target')

    this.setBehavior()
    this.updateTargetZIndex()

    if (this.isOpen) {
      this.initializeDropdownItems()
      this.positionDropdown()
    }
  }

  /**
   * Posiciona o dropdown usando Floating UI
   */
  private positionDropdown() {
    if (this.triggerElement == null || this.targetElement == null) return

    this.floatingManager = new FloatingUIManager({
      triggerElement: this.triggerElement,
      contentElement: this.targetElement,
      placement: this.placement,
    })

    this.floatingManager.start()
  }

  /**
   * Limpa o posicionamento do Floating UI
   */
  private cleanupPositioning() {
    this.floatingManager?.stop()
  }

  /**
   * Atualiza o z-index do target conforme a propriedade targetZIndex
   */
  private updateTargetZIndex() {
    if (this.targetElement === null) {
      return
    }

    if (this.targetZIndex !== null && this.targetZIndex !== undefined) {
      this.targetElement.style.zIndex = this.targetZIndex
    }
  }

  /**
   * Configura os comportamentos de interação do dropdown
   */
  private setBehavior() {
    if (this.triggerElement === null) return

    this.triggerKeydownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        this.handleTriggerBehavior(event)
      } else if (event.key === 'Escape') {
        event.preventDefault()
        this.hide()
        this.focusOnTriggerElement()
      } else if (event.key === 'ArrowDown' && this.isOpen) {
        event.preventDefault()
        this.initializeDropdownItems()
        if (this.dropdownLinks.length > 0) {
          this.focusOnElement(this.dropdownLinks[0])
        } else if (this.dropdownItems.length > 0) {
          this.focusOnElement(this.dropdownItems[0])
        }
      }
    }

    this.targetKeydownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.hide()
        this.focusOnTriggerElement()
      }
    }

    this.triggerElement.addEventListener('keydown', this.triggerKeydownHandler)
    if (this.targetElement !== null) {
      this.targetElement.addEventListener('keydown', this.targetKeydownHandler)
    }
  }

  /**
   * Lida com o comportamento no trigger
   */
  private handleTriggerBehavior(event: Event) {
    event.preventDefault()
    this.toggle(event)

    if (this.isOpen) {
      setTimeout(() => {
        this.initializeDropdownItems()
      }, 0)
    }
  }

  /**
   * Inicializa os itens do dropdown com comportamentos de navegação com teclado,
   * separando claramente entre elementos navegáveis por Tab e elementos navegáveis por setas
   */
  private initializeDropdownItems() {
    if (this.targetElement === null) return

    this.clearDropdownItemListeners()

    this.targetElement.setAttribute('tabindex', '-1')
    this.targetElement.focus()

    const targetSlot = this.el.shadowRoot.querySelector('slot[name="target"]') as HTMLSlotElement
    if (targetSlot === null || targetSlot === undefined) return

    const assignedElements = targetSlot.assignedElements()
    if (assignedElements.length === 0) return

    const nonLinkSelector =
      'button, br-button, input, br-input, br-item:not([href]), textarea, br-textarea, select, br-select, details, [tabindex]:not([tabindex="-1"]):not(a[href])'

    const linkSelector = 'a[href], br-item[href]'

    assignedElements.forEach((element) => {
      if (element instanceof HTMLElement) {
        if (element.matches(linkSelector)) {
          this.dropdownLinks.push(element)
        } else if (element.matches(nonLinkSelector)) {
          this.dropdownItems.push(element)
        }
      }

      const linkChildren = Array.from(element.querySelectorAll(linkSelector)) as HTMLElement[]
      this.dropdownLinks = this.dropdownLinks.concat(linkChildren)

      const nonLinkChildren = Array.from(element.querySelectorAll(nonLinkSelector)) as HTMLElement[]
      this.dropdownItems = this.dropdownItems.concat(nonLinkChildren)
    })

    this.dropdownItems.forEach((item) => {
      item.addEventListener('keydown', this.handleItemKeydown)
    })

    this.dropdownLinks.forEach((link) => {
      link.addEventListener('keydown', this.handleLinkKeydown)
    })

    if (this.dropdownLinks.length > 0) {
      this.focusOnElement(this.dropdownLinks[0])
      this.focusableElement = this.dropdownLinks[0]
    } else if (this.dropdownItems.length > 0) {
      this.focusOnElement(this.dropdownItems[0])
      this.focusableElement = this.dropdownItems[0]
    }
  }

  /**
   * Handler específico para links
   */
  private handleLinkKeydown = (event: KeyboardEvent) => {
    const link = event.currentTarget as HTMLElement
    const key = event.key
    const linkIndex = this.dropdownLinks.indexOf(link)
    const lastLinkIndex = this.dropdownLinks.length - 1

    switch (key) {
      case 'ArrowUp':
      case 'ArrowDown':
      case 'Home':
      case 'End':
        event.preventDefault()
        event.stopPropagation()
        break
      case 'Tab':
        event.preventDefault()

        if (!event.shiftKey) {
          if (linkIndex >= lastLinkIndex) {
            this.focusOnElement(this.dropdownLinks[0])
          } else {
            this.focusOnElement(this.dropdownLinks[linkIndex + 1])
          }
        } else {
          if (linkIndex <= 0) {
            this.focusOnElement(this.dropdownLinks[lastLinkIndex])
          } else {
            this.focusOnElement(this.dropdownLinks[linkIndex - 1])
          }
        }
        break
      case 'Enter':
      case ' ':
        if (key === ' ' && link.tagName.toLowerCase() === 'a') {
          event.preventDefault()
        }
        setTimeout(() => this.hide(), 100)
        break
      case 'Escape':
        event.preventDefault()
        this.hide()
        this.focusOnTriggerElement()
        break
      default:
        break
    }
  }

  /**
   * Handler para eventos de keydown nos itens não-link
   * Implementa navegação completa por setas, Tab não navega entre estes itens
   */
  private handleItemKeydown = (event: KeyboardEvent) => {
    const item = event.currentTarget as HTMLElement
    const key = event.key
    const currentIndex = this.dropdownItems.indexOf(item)
    const lastIndex = this.dropdownItems.length - 1

    switch (key) {
      case 'ArrowUp':
        event.preventDefault()
        if (currentIndex <= 0) {
          this.focusOnElement(this.dropdownItems[lastIndex])
        } else {
          this.focusOnElement(this.dropdownItems[currentIndex - 1])
        }
        break

      case 'ArrowDown':
        event.preventDefault()
        if (currentIndex >= lastIndex) {
          this.focusOnElement(this.dropdownItems[0])
        } else {
          this.focusOnElement(this.dropdownItems[currentIndex + 1])
        }
        break

      case 'Home':
        event.preventDefault()
        this.focusOnElement(this.dropdownItems[0])
        break

      case 'End':
        event.preventDefault()
        this.focusOnElement(this.dropdownItems[lastIndex])
        break

      case 'Enter':
      case ' ':
        event.preventDefault()
        item.click()
        this.hide()
        this.focusOnTriggerElement()
        break

      case 'Tab':
        break

      case 'Escape':
        event.preventDefault()
        this.hide()
        this.focusOnTriggerElement()
        break

      default:
        break
    }
  }

  private getCssClassMap(): CssClassMap {
    return {
      'br-dropdown': true,
    }
  }

  /**
   * Alterna o estado do dropdown entre aberto e fechado.
   */
  private readonly toggle = (event: Event) => {
    event.stopPropagation()
    this.isOpen = !this.isOpen
  }

  /**
   * Método que lida com cliques fora do dropdown.
   *
   * @param {MouseEvent} event - O evento de clique que ocorreu.
   */
  private onClickOutside(event: MouseEvent) {
    if (!this.isOpen) return

    const path = event.composedPath()

    if (
      this.triggerElement &&
      this.targetElement &&
      !path.includes(this.triggerElement) &&
      !path.includes(this.targetElement)
    ) {
      this.hide()
    }
  }

  /**
   * Abre o dropdown.
   * Define a propriedade `isOpen` como verdadeira e retorna o novo estado.
   *  Este método pode ser chamado externamente.
   */
  @Method()
  async open(): Promise<{ isOpen: boolean }> {
    this.isOpen = true
    return { isOpen: this.isOpen }
  }

  /**
   * Esconde o dropdown.
   * Define a propriedade `isOpen` como falsa e retorna o novo estado.
   * Este método pode ser chamado externamente.
   */
  @Method()
  async hide(): Promise<{ isOpen: boolean }> {
    this.isOpen = false
    return { isOpen: this.isOpen }
  }

  /**
   * Foca em um elemento específico com suporte a componentes personalizados
   * @param targetElement Elemento a receber o foco
   */
  private focusOnElement(targetElement: HTMLElement) {
    if (targetElement && targetElement !== this.el) {
      if ('setFocus' in targetElement) {
        ;(targetElement as { setFocus: () => void }).setFocus()
      } else {
        targetElement.focus()
      }
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    }
  }

  /**
   * Foca no elemento interativo dentro do slot trigger
   * Esta função procura pelo elemento dentro do slot trigger e aplica o foco nele
   */
  private focusOnTriggerElement() {
    const triggerSlot = this.el.shadowRoot.querySelector('slot[name="trigger"]') as HTMLSlotElement
    if (triggerSlot !== null) {
      const slotElements = triggerSlot.assignedElements()
      if (slotElements.length > 0) {
        const firstElement = slotElements[0] as HTMLElement
        if ('setFocus' in firstElement) {
          ;(firstElement as { setFocus: () => void }).setFocus()
        } else {
          firstElement.focus()
        }
        firstElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
      } else {
        this.focusTrigger()
      }
    } else {
      this.focusTrigger()
    }
  }

  /**
   * Auxiliar para focar no elemento trigger
   */
  private focusTrigger() {
    if (typeof this.triggerElement !== 'undefined') {
      this.triggerElement.focus()
      this.triggerElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    }
  }

  /**
   * Define o foco no elemento interno do componente.
   * Este método pode ser chamado externamente para garantir que o foco seja aplicado ao elemento correto.
   */
  @Method()
  async setFocus() {
    if (this.focusableElement !== null && this.focusableElement !== undefined) {
      this.focusableElement.focus()
    } else {
      this.focusOnTriggerElement()
    }
  }

  render() {
    return (
      <Host is-open={this.isOpen} placement={this.placement} prevent-auto-dismiss={this.preventAutoDismiss}>
        <div class={this.getCssClassMap()} id={this.customId}>
          <div
            id="trigger"
            onClick={this.toggle}
            aria-controls="target"
            aria-haspopup="true"
            aria-expanded={this.isOpen ? 'true' : 'false'}
          >
            <slot name="trigger"></slot>
          </div>

          <div id="target" aria-hidden={this.isOpen ? 'false' : 'true'}>
            <slot name="target"></slot>
          </div>
        </div>
      </Host>
    )
  }
}
